/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Vendored BookingPress/theme CSS (src/styles/vendor/bookingpress/). Must be
// the FIRST import: these styles used to load as <head> links before the
// bundle, so every app override (settings.scss, scoped styles) must stay
// later in the cascade.
import '@/styles/vendor/bookingpress/index.css';

// Plugins
import { hasSensitiveQuery, registerPlugins, scrubBreadcrumb, scrubDeep } from '@/plugins'

// Components
import App from './App.vue'
import { createPinia } from "pinia";

import VueTelInput from 'vue-tel-input';
// Self-hosted Roboto — same six weights the old Google-Fonts link served.
// The build must not depend on fonts.googleapis.com (follow-up to the
// BookingPress CSS vendoring, spec 2026-07-10).
import '@fontsource/roboto/100.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/900.css';
import 'vue-tel-input/vue-tel-input.css';


// Composables
import { createApp } from 'vue'

import * as Sentry from "@sentry/vue";

import { analytics, initAnalytics, stepLabel } from '@/analytics';
import { useBookingStepStore } from '@/stores/bookingStep';
import { useBookingDataStore } from '@/stores/bookingData';

// The greenhouse feature flag is gone — La Serre is fully live. Drop the
// legacy localStorage key so browsers that once opened a ?greenhouse=0 link
// don't carry stale state around (it no longer drives anything; pure hygiene).
localStorage.removeItem('ENABLE_GREENHOUSE');

const pinia = createPinia();
pinia.use(Sentry.createSentryPiniaPlugin());

const app = createApp(App).use(pinia);

// EAUG-77 §10: edit links (?mode=edit&token=…) and menu links
// (?mode=menu&token=…) carry a credential in this iframe's URL, and the
// order-edit API calls carry it in their query string. Every event,
// transaction and breadcrumb is scrubbed. Session Replay's DOM recording
// keeps the page URL where no hook can rewrite it, so a load with a
// credential in its URL records no replay.
const recordReplay = !hasSensitiveQuery(window.location.search);

Sentry.init({
  app,
  dsn: "https://8f2e0091493643e49cda807e5395d5c6@o229109.ingest.us.sentry.io/4508400108240896",
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(recordReplay ? [Sentry.replayIntegration({ beforeAddRecordingEvent: (event) => scrubDeep(event) })] : []),
  ],
  beforeSend: (event) => scrubDeep(event),
  beforeSendTransaction: (event) => scrubDeep(event),
  beforeBreadcrumb: (breadcrumb) => scrubBreadcrumb(breadcrumb),
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\reservation\.eauget\.com/],
  // Session Replay
  replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

app.use(VueTelInput);

registerPlugins(app);

initAnalytics();
// Step funnel: the store dispatches a bare 'stepChanged' event on every
// transition; read the current step + booking context from the stores.
// wizard_start is emitted separately from booking.vue's onMounted (the entry
// point for the normal fresh-load path) — never here, to avoid a double-fire.
document.addEventListener('stepChanged', () => {
  try {
    const step = useBookingStepStore();
    const data = useBookingDataStore();
    analytics.track('step_view', {
      step: stepLabel(String(step.step)),
      mode: String(step.getMode()).toLowerCase(),
      venue: data.venue || null,
    });
  } catch { /* pinia not ready yet — ignore */ }
});

app.mount('#app')
